import { Component } from 'react';
import { Accordion, Button, Card, Form, Container, Row, Col, Alert } from "react-bootstrap";
import GameData from "../mobx/GameData";
import { observe, reaction } from 'mobx';
import ApplicationState from "../mobx/ApplicationState";

import { ChrFile } from "../components/nes/ChrFile";

import './GraphicsPage.scss';
import GraphicsPatcher from "../components/services/GraphicsPatcher";
import { PalettePreview } from "../components/nes/PalettePreview";

import aboutMenu from '../images/aboutmenu.png';


export default class GraphicsPage extends Component {

    thisId = null;
    gameDataCleanup = null;
    exampleChrLink = null;
    examplePalLink = null;
    BuiltInTilesets = [];
    BuiltInSprites = [];
    emulatorVisibleReaction = null;
    currentTileset = {};
    currentTilesetPalettes = {};

    // eslint-disable-next-line no-useless-constructor
    constructor(props) {
        super(props);

        this.state = {
            useBuiltInGraphics: !!GameData.currentGame.useBuiltInGraphics,
            builtInTileId: GameData.currentGame.builtInTileId,
            builtInSpriteId: GameData.currentGame.builtInSpriteId,

            loading: true
        };

        this.gameData = {
            gameGraphicsData: GameData.currentGame.gameGraphicsData,
            gamePaletteData: GameData.currentGame.gamePaletteData,
            spritePalette: GameData.currentGame.spritePalette,
        };
    }

    async componentDidMount() {
        this.gameDataCleanup = observe(GameData, () => { 
            this.forceUpdate()
        });

        this.emulatorVisibleReaction = reaction(
            () => ApplicationState.emulatorIsVisible,
            ((isVisibleNow, wasVisibleBefore) => {
                if (isVisibleNow !== wasVisibleBefore) {
                    this.forceUpdate();
                }
            })
        );

        const BaseGame = await import('../data/BaseGame');
        this.BuiltInTilesets = (await import('../data/BuiltInTilesets')).default;
        this.BuiltInSprites = (await import('../data/BuiltInSprites')).default;
        this.exampleChrLink = `data:application/binary;base64,${btoa(String.fromCharCode.apply(null, BaseGame.BaseGraphics))}`;
        this.examplePalLink = `data:application/binary;base64,${btoa(String.fromCharCode.apply(null, BaseGame.BaseGamePalette))}`;
        this.rebuildCurrentTileset();
        this.setState({loading: false});
    }

    componentWillUnmount() {
        if (this.gameDataCleanup) {
            this.gameDataCleanup();
        }

        if (this.emulatorVisibleReaction) {
            this.emulatorVisibleReaction();
        }

    }

    rebuildCurrentTileset() {
        this.currentTileset = this.BuiltInTilesets.find(t => t.id === this.state.builtInTileId);
        this.currentTilesetPalettes = [
            this.currentTileset.palette.slice(0, 4),
            this.currentTileset.palette.slice(4, 8),
            this.currentTileset.palette.slice(8, 12),
            this.currentTileset.palette.slice(12, 16)
        ];
        this.forceUpdate();
    }
    
    async fieldChanged() {

        await new Promise(resolve => this.setState({
            useBuiltInGraphics: document.getElementById('use-builtin-art-yes').checked,
        }, resolve));

        if (this.state.useBuiltInGraphics) {
            await new Promise(resolve => { 
                this.setState({
                    builtInTileId: parseInt(document.getElementById('artStyle').value, 10),
                    builtInSpriteId: parseInt(document.getElementById('spriteStyle').value, 10)
                }, resolve);
            });
            this.rebuildCurrentTileset();
        }

        ApplicationState.setCurrentPageUnsaved();
    }

    async fileUpload(eType, event) {
        let field, length;
        switch (eType) {
            case 'chrFile': field = 'gameGraphicsData'; length = 4096; break;
            case 'palFile1': field = 'gamePaletteData'; length = 16; break;
            case 'palFile2': field = 'spritePalette'; length = 16; break;
            default: throw new Error('Unknown eType: ' + eType);
        }
        const file = document.getElementById(eType).files[0];
        let data = new Uint8Array(await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result)
            reader.onerror = (e) => reject(e);

            reader.readAsArrayBuffer(file);
        }));

        if (data.length !== length) {
            ApplicationState.addAlert('danger', 'Unable to load file - it must be exactly ' + length + ' bytes in size! Check the FAQ at the bottom for help.');
            return;
        }

        if (eType === 'palFile2') {
            // Strip the extra stuff out
            data = data.slice(0, 4);
        }

        this.gameData[field] = Array.from(data);
        this.forceUpdate();


        ApplicationState.setCurrentPageUnsaved();
    }
    
    async saveChanges(event) {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        let gamePaletteData, spritePalette;

        if (this.state.useBuiltInGraphics) {
            const tileArt = this.BuiltInTilesets.find(t => t.id === this.state.builtInTileId);
            const spriteArt = this.BuiltInSprites.find(s => s.id === this.state.builtInSpriteId);

            gamePaletteData = tileArt.palette;
            spritePalette = spriteArt.palette;
        } else {
            gamePaletteData = this.gameData.gamePaletteData;
            spritePalette = this.gameData.spritePalette;
        }

        let updatedGame = {
            ...GameData.currentGame,
            useBuiltInGraphics: this.state.useBuiltInGraphics,
            builtInTileId: this.state.builtInTileId,
            builtInSpriteId: this.state.builtInSpriteId,
            gamePaletteData, 
            spritePalette
        };

        // Need to rewrite builtInGraphics in this case, since it builds off the game settings we just updated.
        if (this.state.useBuiltInGraphics) {
            updatedGame.gameGraphicsData = await GraphicsPatcher.getPatchedGraphicsDataForGame(updatedGame);
            updatedGame.tileData = JSON.parse(JSON.stringify(this.BuiltInTilesets.find(t => t.id === this.state.builtInTileId).tileData));
            updatedGame.creditsScreenData = await GraphicsPatcher.getPatchedCreditsDataForGame(updatedGame);
        } else {
            updatedGame.gameGraphicsData = this.gameData.gameGraphicsData;
        }

        GameData.replaceCurrentGame(updatedGame);

        // SUCCESS! Share the update!
        ApplicationState.addAlert('success', 'Graphics updated successfully.');
        ApplicationState.unsetCurrentPageUnsaved();
        
    }

    render() {
        let mainForm
        if (this.state.loading || ApplicationState.emulatorIsVisible) { return <></>; }
        if (this.state.useBuiltInGraphics) {
            mainForm = (<>
                <h3 className="mb-2 mt-3">Built-in art selection</h3>

                {(GameData.currentGame.useBuiltInGraphics !== this.state.useBuiltInGraphics) ? (
                    <Alert variant="warning"><strong>Warning:</strong> Going back to built-in art after using custom art may cause undesired behavior! Saving this way will re-add the authors to the credits screen, and also rewrite the information about all of your tiles.</Alert>
                ) : ''}

                <Form.Group className="mb-3">
                    <Form.Label htmlFor="spriteStyle">Player Sprite</Form.Label>
                    <Form.Select arial-label="Select a sprite" onChange={() => this.fieldChanged()} defaultValue={this.state.builtInSpriteId} id="spriteStyle">
                        {this.BuiltInSprites.map(opt => {
                            return <option key={opt.id} value={opt.id}>{opt.title}</option>
                        })}
                    </Form.Select>
                    <strong>Preview</strong>
                    <div className="chr-scissors sm">
                        <ChrFile data={this.BuiltInSprites.find(t => t.id === this.state.builtInSpriteId).data} palette={this.BuiltInSprites.find(t => t.id === this.state.builtInSpriteId).palette} />
                    </div>
                </Form.Group>


                <Form.Group className="mb-3">
                    <Form.Label htmlFor="artStyle">Game Tiles</Form.Label>
                    <Form.Select arial-label="Select a tileset" onChange={() => this.fieldChanged()} defaultValue={this.state.builtInTileId} id="artStyle">
                        {this.BuiltInTilesets.map(opt => {
                            return <option key={opt.id} value={opt.id}>{opt.title}</option>
                        })}
                    </Form.Select>
                    <div className="mt-2 mb-1">Preview</div>
                    <div className="tile-holder">
                        {this.currentTileset.tileData.map((tileDat, idx) => (
                            <ChrFile key={'tile'+idx} data={this.currentTileset.data} palette={this.currentTilesetPalettes[tileDat.palette]} isMeta={true} tileId={idx} />
                        ))}
                    </div>
                    <Alert variant="warning"><strong>Warning:</strong> Be careful changing this after creating a game! Switching between the built-in tilesets may break your levels, since tile behavior may not be the same between sets. If you don't have many modifications yet, it may be better to create a new game from the home page.</Alert>
                </Form.Group>
                
                <div className="mt-5 game-cards">
                    <h2 className="mb-3">Built-in Art Credits</h2>
                    {[...this.BuiltInTilesets, ...this.BuiltInSprites].map(art => (
                        <Card key={art.id + ' ' + art.title} className="mb-3">
                            <Card.Body>
                                <Card.Title><a target="_blank" rel="noreferrer" href={art.link}>{art.title}</a></Card.Title>
                                    <table className="mt-3"><tbody>
                                        <tr>
                                            <td>Author: </td>
                                            <td><a target="_blank" rel="noreferrer" href={art.authorLink}>{art.author}</a></td>
                                        </tr>
                                        <tr>
                                            <td>Source: </td>
                                            <td><a target="_blank" rel="noreferrer" href={art.sourceLink}>{art.source}</a></td>
                                        </tr>
                                        <tr>
                                            <td>License: </td>
                                            <td><a target="_blank" rel="noreferrer" href={art.licenseLink}>{art.license}</a></td>
                                        </tr>
                                    </tbody></table>
                            </Card.Body>
                        </Card>
                    ))}
                </div>


            </>);
        } else {
                mainForm = (<>
                    <h3 className="mb-2 mt-3">Custom art</h3>
                    <p>You can upload and tweak your custom art below. For now, art must be created in a NES-native format. See the FAQ at the bottom for details, and links to tools you can use!</p>
                    <Container className="upload-form">
                        <Row>
                            <Col md={8} sm={12}>
                                <Form.Group controlId="chrFile" className="mb-3">
                                    <Form.Label>Tile Data (NES .chr format, 4kb)</Form.Label>
                                    <Form.Control type="file" name="chrFile" accept=".chr,.bin" onChange={event => this.fileUpload('chrFile', event)} />
                                </Form.Group>
                            </Col>
                            <Col>
                                <div className="preview">
                                    <ChrFile data={this.gameData.gameGraphicsData} palette={this.gameData.gamePaletteData} />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={8} sm={12}>
                                <Form.Group controlId="palFile1" className="mb-3">
                                    <Form.Label>Tile Palette (NES .pal format, 16 bytes)</Form.Label>
                                    <Form.Control type="file" name="chrFile" accept=".pal,.bin" onChange={event => this.fileUpload('palFile1', event)} />
                                </Form.Group>
                            </Col>
                            <Col>
                                <div className="preview palette-group preview-a">
                                    {[0, 1, 2, 3].map(paletteId => (
                                        <PalettePreview key={paletteId} palette={this.gameData.gamePaletteData.slice((paletteId*4), (paletteId*4) + 4)} />
                                    ))}
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={8} sm={12}>
                                <Form.Group controlId="palFile2" className="mb-3">
                                    <Form.Label>Sprite Palette (NES .pal format, 16 bytes)</Form.Label>
                                    <Form.Control type="file" name="chrFile" accept=".pal,.bin" onChange={event => this.fileUpload('palFile2', event)} />
                                    <small className="actually-small"><strong>Note:</strong> Only the first palette in the file will be used; the rest will be discarded. If you are generating this manually, fill the rest of the bytes with whatever you like.</small>
                                </Form.Group>

                            </Col>
                            <Col>
                                <div className="preview preview-b">
                                    <PalettePreview palette={this.gameData.spritePalette.slice(0, 4)} />
                                </div>

                            </Col>
                        </Row>

                    </Container>

                </>)
        }

        return (
            <Container className="graphics-page">
                <Row>
                    <Col>

                        <h1>Graphics</h1>

                        <p>Configure your game's graphics here. You can choose from a few built-in graphic packs, or if you're feeling adventurous, you can also edit your own!</p>

                        <Form noValidate ref={this.form} onSubmit={event => this.saveChanges(event)}>

                            <Form.Check type="radio" id="use-builtin-art-yes" label="Use Built-in art" defaultChecked={this.state.useBuiltInGraphics} name="useBuiltInArt" onChange={() => this.fieldChanged()} />
                            <Form.Check type="radio" id="use-builtin-art-no" label="Use Custom art" defaultChecked={!this.state.useBuiltInGraphics} name="useBuiltInArt" onChange={() => this.fieldChanged()} />

                            {mainForm}

                            <div className="button-footer-bar">
                                <Button variant="primary" type="submit" onClick={event => this.saveChanges(event)}>Save Changes</Button>
                            </div>
                        </Form>

                        {this.state.useBuiltInGraphics ? '' : (
                            <Accordion className="mt-3">
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header>How can I create these files?</Accordion.Header>
                                    <Accordion.Body>
                                        <p>
                                            These files can be created using tools for creating NES Graphics, such as{' '}
                                            <a target="_blank" rel="noreferrer" href="https://www.patreon.com/posts/bug-fixes-nexxt-51044747">NEXXT</a>
                                            {' '}or{' '}
                                            <a target="_blank" rel="noreferrer" href="https://shiru.untergrund.net/software.shtml">NESST</a>. 
                                        </p>

                                        <p>
                                            Here is an example chr file you can build off of: <a href={this.exampleChrLink} download="Example.chr">Download Example</a>. 
                                            (The tools have default palettes that work, so you do not need these.)
                                        </p>

                                        <p>
                                            These tools limit how you can design art, to make sure it fits the requirements of the NES. To start editing, open the 
                                            editor program, then from the "Patterns" menu, go to "open CHR" and find the example file you downloaded. You should now 
                                            see some simple graphics on the right. You can update any tiles in this file as you see fit. 
                                        </p>
                                        <p>
                                            You can also export this content into a bitmap (.bmp) file, which can be edited in any image editor. 
                                            There is another FAQ question about this.
                                        </p>
                                        <p>
                                            Once you are done editing, you can create the tile data "chr" file this editor understands by going to the "Patterns" menu, and
                                            clicking "Save Chr -&gt; 4K (this table)". You can upload this file in the "Tile Data" section above. You also likely 
                                            changed the colors in the lower right - these should be saved separately and uploaded as a palette. Go to the "Palettes"
                                            menu, and choose "Save Current Set", then save this anywhere. You can upload this as a tile palette or a sprite palette.
                                        </p>
                                    </Accordion.Body>
                                </Accordion.Item>
                                <Accordion.Item eventKey="1">
                                    <Accordion.Header>Why do I have to use 4 colors?</Accordion.Header>
                                    <Accordion.Body>
                                        <p>
                                            The NES has a very limited set of colrs that it can show. It can show exactly 4 colors in a 16x16 pixel area. It cannot pick 4
                                            arbitrary colors however - it only knows how to show colors in one of four palettes, which are basically groups of colors. This
                                            is where the limited number of colors comes from.
                                        </p>
                                        <p>
                                            You can see this demonstrated by opening one of the editors linked in the previous question. The graphics on the right are your
                                            "tiles" - you can apply any of the 4 "palettes" in the lower right to your tiles to see how it looks.
                                            
                                        </p>
                                        <p>
                                            If you want to test out how the NES actually displays the tiles, you can place tiles from the section on the right into the big
                                            section on the left. It will place the tile and palette you select onto the screen, then force the screen to update to stay within
                                            NES limitations. Explaining this with words is very hard - I suggest opening one of the graphics tools and experimenting to learn 
                                            this yourself.
                                        </p>
                                    </Accordion.Body>
                                </Accordion.Item>     
                                <Accordion.Item eventKey="2">
                                    <Accordion.Header>Are there example files I can build from?</Accordion.Header>
                                    <Accordion.Body>
                                        <p>
                                            There are! 
                                        </p>
                                        <p>
                                            Here is an example chr file: <a href={this.exampleChrLink} download="Example.chr">Download Example</a>
                                        </p>
                                        <p>
                                            The NES tools have paletes built in, but if you want to start from the default that we use, you can{' '} 
                                            <a href={this.examplePalLink} download="example.pal">download the base palette</a> too. 
                                        </p>
                                    </Accordion.Body>
                                </Accordion.Item>
                                <Accordion.Item eventKey="3">
                                    <Accordion.Header>Can I create graphics using a normal image editor?</Accordion.Header>
                                    <Accordion.Body>
                                        <p>
                                            You can! This is a little bit more complicated however, and you should have some understanding of how NES graphics work. You
                                            will need to limit yourself to 4 colors total. (The NES will apply the other palette groups, but normal image editors cannot understand
                                            this.)
                                        </p>
                                        <p>
                                            First, you will need to open the chr file in one of the image editors in the first question. We have to convert this image to a format
                                            that image editors understand. (See the first question for details on how to open it.) Next go to "File" and from the "Export" 
                                            submenu, choose "Tileset as BMP". This will save a bitmap file (.bmp) that any editor can understand, from Paint to Photoshop.
                                        </p>
                                        <p>
                                            Edit your file using 4 colors until you are happy with it. Once you are done, you will need to import it back into your NES graphics editor,
                                            to convert it back to a NES-friendly format. From the "File" menu, look in the "Import" menu, and select "BMP as Tileset". Load your
                                            edited file, and you should see it show up in the section on the right.
                                        </p>
                                        <p>
                                            From here, you can follow the steps in the first FAQ question to save your file and load it in this editor.
                                        </p>
                                    </Accordion.Body>
                                </Accordion.Item>
                                <Accordion.Item eventKey="4">
                                    <Accordion.Header>What areas can I edit in the chr file? What do they do?</Accordion.Header>
                                    <Accordion.Body>
                                        <p>
                                            There are 4 main sections in the chr file you can edit. Here is an image breaking them down: 
                                        </p>
                                        <p>
                                            <img src={aboutMenu} alt="Breakdown of types" />
                                        </p>
                                        <ul>
                                            <li><strong>Tiles:</strong>The tiles that make up your levels. You can update the palettes and collisions on the "Map Tiles" page.</li>
                                            <li><strong>Player:</strong>This is the player tile. It will animate when moving based on the tiles shown in order here.</li>
                                            <li><strong>Text:</strong>This is the font for your game. It is in ascii order, and must stay in that order.</li>
                                            <li><strong>Menu:</strong>The last few lines of the image are used to form the borders around menus in the game. These can be changed as needed.</li>
                                        </ul>
                                    </Accordion.Body>
                                </Accordion.Item>
                                <Accordion.Item eventKey="5">
                                    <Accordion.Header>What sections can I put my own graphics in?</Accordion.Header>
                                    <Accordion.Body>
                                        <p>
                                            Unfortunately, most of the built-in graphics are already used. There are only a few safe areas. 
                                        </p>
                                        <p>
                                            The space to the right of the player sprite is completely unused. If you don't use all of your level tiles,
                                            you can also replace a few of these.
                                        </p>
                                        <p>
                                            Finally, the last two rows of the file have a few blank areas that should
                                            be safe. The two brackets, the hearts, nad the dollar sign are also unused right now.
                                        </p>
                                    </Accordion.Body>
                                </Accordion.Item>


                            </Accordion>
                        )}
                    </Col>
                </Row>
            </Container>
        );
    }
}